import { searchTypes } from 'constants/common';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileTypes } from 'types/baseTypes';
import { isPermissionDenied } from 'utils/apiUtils';
import { useAppSelector } from '../../../hooks/typedHooks';
import { useLazyGetReceiptsQuery } from '../api';
import { Receipt } from '../receipt.model';
import { ReceiptColumnInfo, ReceiptSearchKey, ReceiptStatus } from '../receipts.model';
import { DEFAULT_RECEIPT_COLUMN_CONFIG } from '../constants';

export interface ReceiptQueryParams {
  status: ReceiptStatus[];
  tipAmountNE: number | null;
  createdBy: string[];
  customerId: string[];
  type: FileTypes | null;
  paymentMethod: string[];
  createdAtFrom: string | null;
  createdAtTo: string | null;
  search: string | null;
  sourceType: string[];
  amount?: string | null;
  sortBy?: string;
  sortOrder?: string;
  limit?: number | null;
}

export const initialQueryParamsState: ReceiptQueryParams = {
  status: [],
  tipAmountNE: null,
  createdBy: [],
  customerId: [],
  paymentMethod: [],
  createdAtFrom: null,
  createdAtTo: null,
  search: null,
  sourceType: [],
  type: null,
  amount: null,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  limit: null
};

const DEFAULT_LIMIT = 50;

export const usePaginatedGetReceiptsQuery = (
  initialPageSize: number = DEFAULT_LIMIT,
  initialParams?: Partial<ReceiptQueryParams>
) => {
  const [list, setList] = useState<Receipt[]>([]);
  const [queryParams, setQueryParams] = useState<ReceiptQueryParams>({
    ...initialQueryParamsState,
    ...initialParams,
    limit: initialPageSize
  });
  const [columnConfig, setColumnConfig] = useState<ReceiptColumnInfo[]>(DEFAULT_RECEIPT_COLUMN_CONFIG);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [totalListCount, setTotalListCount] = useState<number>(0);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const page = useRef(1);
  const [getReceiptsData, receiptsState] = useLazyGetReceiptsQuery();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [searchType, setSearchType] = useState(searchTypes[1]);
  const queryParamsRef = useRef(queryParams);
  queryParamsRef.current = queryParams;

  const queryParamsKey = useMemo(
    () => JSON.stringify({ ...queryParams, searchType: searchType.type, teamId: currentTeam?.id }),
    [currentTeam?.id, queryParams, searchType.type]
  );

  useEffect(() => {
    if (!receiptsState?.isFetching && receiptsState.isSuccess && receiptsState?.data) {
      setHasPermissionsToView(true);
      const receipts = receiptsState.data.transactions ?? [];
      setList(receipts);
      setTotalListCount(receiptsState.data.filteredCount ?? 0);

      if (receiptsState.data.columnInfo && Array.isArray(receiptsState.data.columnInfo)) {
        const sortedColumnInfo = [...receiptsState.data.columnInfo].sort((a, b) => a.priority - b.priority);
        setColumnConfig(sortedColumnInfo);
      }

      setIsLoading(false);
    }
  }, [receiptsState?.isFetching, receiptsState?.isSuccess, receiptsState?.data]);

  useEffect(() => {
    if (!receiptsState?.isFetching && receiptsState?.isError) {
      setIsLoading(false);
      setIsError(true);
      if (isPermissionDenied(receiptsState.error?.['data']?.code, receiptsState.error?.['data']?.message)) {
        setHasPermissionsToView(false);
      }
      setList([]);
      setTotalListCount(0);
    }
  }, [receiptsState?.isFetching, receiptsState?.isError]);

  const fetchData = useCallback(
    (pageNumber: number) => {
      setIsLoading(true);
      setIsError(false);
      setCurrentPage(pageNumber);
      page.current = pageNumber;

      const params = queryParamsRef.current;
      const currentLimit = params.limit ?? initialPageSize;
      const isSearchPresent = params.search !== null && params.search !== '';

      if (currentTeam?.id) {
        const { search, type, amount, ...rest } = params;
        getReceiptsData({
          teamId: currentTeam.id,
          limit: currentLimit,
          page: pageNumber - 1,
          source: 'WEB',
          ...rest,
          amount: amount || null,
          searchKey: isSearchPresent ? (searchType.type as ReceiptSearchKey) : null,
          searchValue: isSearchPresent ? params.search.trim() : null
        });
      } else {
        setIsLoading(false);
        setList([]);
        setTotalListCount(0);
      }
    },
    [currentTeam?.id, getReceiptsData, initialPageSize, searchType.type]
  );

  const resetListAndFetch = useCallback(() => {
    setCurrentPage(1);
    page.current = 1;
    setList([]);
    fetchData(1);
  }, [fetchData]);

  useEffect(() => {
    resetListAndFetch();
  }, [queryParamsKey, resetListAndFetch]);

  const resetQuery = useCallback(() => {
    setCurrentPage(1);
    page.current = 1;
    setList([]);
    setQueryParams({ ...initialQueryParamsState, limit: initialPageSize });
    setColumnConfig(DEFAULT_RECEIPT_COLUMN_CONFIG);
    setIsError(false);
    setTotalListCount(0);
  }, [initialPageSize]);

  return {
    isLoading,
    isFetching: receiptsState?.isFetching,
    totalListCount,
    list,
    columnConfig,
    isError,
    fetchData,
    resetQuery,
    resetListAndFetch,
    currentPage,
    setQueryParams,
    queryParams,
    isLoadingSuccess: receiptsState.isSuccess,
    hasPermissionsToView,
    searchType,
    setSearchType,
    limit: queryParams.limit ?? initialPageSize,
    page: currentPage
  };
};
